import { useNavigate } from "react-router-dom";
import {
  IconAlertTriangle,
  IconBuildingSkyscraper,
  IconMapPin,
  IconReceipt2,
} from "@tabler/icons-react";
import { EmptyState, type EmptyStateAction } from "../../../../components/common/empty-state";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { CheckoutAddressKind, CheckoutError } from "../../utils/checkout-error";

const TITLE: Record<CheckoutAddressKind, string> = {
  delivery: "Choose a delivery address",
  billing: "Your billing address is incomplete",
  company: "Complete your company address",
};

const ICON: Record<CheckoutAddressKind, typeof IconMapPin> = {
  delivery: IconMapPin,
  billing: IconReceipt2,
  company: IconBuildingSkyscraper,
};

interface CheckoutBlockedViewProps {
  error: CheckoutError;
  /** Cart path only — returns to the delivery step. */
  onChangeDeliveryAddress?: () => void;
}

/**
 * Shown on the payment step when the order can't be paid yet — the BE
 * refused to create the draft order or the payment intent (most often a
 * missing / incomplete address). Says what to fix and links there; the card
 * is never charged in this state.
 */
export function CheckoutBlockedView({ error, onChangeDeliveryAddress }: CheckoutBlockedViewProps) {
  const navigate = useNavigate();

  const backToDelivery: EmptyStateAction | undefined = onChangeDeliveryAddress && {
    label: "Back to delivery",
    leftIcon: <IconMapPin size={16} />,
    onClick: onChangeDeliveryAddress,
  };
  const primaryAction: EmptyStateAction | undefined =
    error.address === "company"
      ? {
          label: "Complete company profile",
          leftIcon: <IconBuildingSkyscraper size={16} />,
          onClick: () => navigate(AppRoutes.ACCOUNT),
        }
      : backToDelivery;

  return (
    <EmptyState
      icon={error.address ? ICON[error.address] : IconAlertTriangle}
      title={error.address ? TITLE[error.address] : "We couldn't start your payment"}
      description={`${error.message} Your card hasn't been charged.`}
      primaryAction={primaryAction}
      secondaryAction={primaryAction !== backToDelivery ? backToDelivery : undefined}
      actionable={false}
      className="w-full rounded-2xl border border-borderDefault bg-bgWhite"
    />
  );
}
